// src/components/BucksAdminDashboard.js (Updated with Recipes Tab)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../App';
import '../stylings/BucksAdminDashboard.css';
import BucksAdminInventory from './BucksAdminInventory'; // Import the inventory component
import BucksAdminOrderManage from './BucksAdminOrderManage'; // Import our new component
import BucksAdminCustomerManage from './BucksAdminCustomerManage'; // Import customer management component
import BucksAdminReports from './BucksAdminReports'; // Import reports component
import BucksAdminAnnouncements from './BucksAdminAnnouncements'; // Import announcements component
import BucksAdminRecipes from './BucksAdminRecipes'; // Import the new recipes component
import BucksAdminWorkers from './BucksAdminWorkers'; // Import the new workers component


function BucksAdminDashboard() {
  // Get authentication context (which now uses sessionStorage under the hood)
  const { adminUser, logout } = useAuth();
  
  // State for active section
  const [activeSection, setActiveSection] = useState('dashboard');
  
  // State for dashboard metrics
  const [metrics, setMetrics] = useState({
    totalProducts: 0,
    lowStock: 0,
    totalValue: 0,
    recentOrders: 0
  });
  
  // Loading states for metrics
  const [loading, setLoading] = useState({
    totalProducts: true,
    lowStock: true,
    announcements: true
  });
  
  // State for notifications
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // State for low stock items (for dashboard display)
  const [lowStockItems, setLowStockItems] = useState([]);
  
  // State to store inventory data that can be shared
  const [inventoryData, setInventoryData] = useState([]);
  
  // State for dashboard announcements
  const [dashboardAnnouncements, setDashboardAnnouncements] = useState([]);

  // Create refs to hold the interval IDs for cleanup
  const intervalsRef = useRef({});
  
  // Flag to track if dashboard is focused/visible
  const [isDashboardActive, setIsDashboardActive] = useState(true);
  
  // Last update timestamp
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Refresh rates in milliseconds
  const FAST_REFRESH_RATE = 1 * 60 * 1000; // 30 seconds when dashboard is active
  const SLOW_REFRESH_RATE = 5 * 60 * 1000; // 5 minutes when dashboard is not active
  
  // Memoize the updateMetrics function with useCallback - MODIFIED TO PREVENT OVERRIDING LAMBDA METRICS
  const updateMetrics = useCallback((newMetrics) => {
    // Only update totalValue and recentOrders from the component
    // This ensures that totalProducts and lowStock always come from our Lambda functions
    setMetrics(prevMetrics => ({
      ...prevMetrics,
      totalValue: newMetrics.totalValue || prevMetrics.totalValue,
      recentOrders: newMetrics.recentOrders || prevMetrics.recentOrders
    }));
  }, []);
  
  // Memoize updateInventoryData function
  const updateInventoryData = useCallback((data) => {
    setInventoryData(data);
  }, []);

  // Create a global refresh function
  const refreshAllDashboardData = useCallback(() => {
    setLastUpdated(new Date());
    fetchTotalProducts();
    fetchLowStockCount();
    fetchDashboardAnnouncements();
  }, []);

  // Add visibility change listener to adjust refresh rates based on tab visibility
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      setIsDashboardActive(isVisible);
      
      // If becoming visible, refresh data immediately
      if (isVisible && activeSection === 'dashboard') {
        refreshAllDashboardData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshAllDashboardData, activeSection]);
  
  // Monitor active section changes to adjust refresh behavior
  useEffect(() => {
    if (activeSection === 'dashboard') {
      // Refresh immediately when switching to dashboard
      refreshAllDashboardData();
    }
  }, [activeSection, refreshAllDashboardData]);
  
  // Function to fetch the total products count
  const fetchTotalProducts = async () => {
    // Set loading state to true when fetch starts
    setLoading(prev => ({ ...prev, totalProducts: true }));
    
    try {
      const response = await fetch('https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-dashboard-total-products-metric-get');
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      
      // Update only the totalProducts property, keeping other metrics unchanged
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        totalProducts: data.totalProducts
      }));
    } catch (error) {
      console.error('Error fetching total products count:', error);
      // Set a notification for the error
      setNotification({
        show: true,
        message: 'Failed to load total products count',
        type: 'error'
      });
    } finally {
      // Set loading state to false when fetch completes (regardless of success/failure)
      setLoading(prev => ({ ...prev, totalProducts: false }));
    }
  };
  
  // Function to fetch the low stock products count
  const fetchLowStockCount = async () => {
    // Set loading state to true when fetch starts
    setLoading(prev => ({ ...prev, lowStock: true }));
    
    try {
      const response = await fetch('https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-dashboard-low-stock-products-metric-get');
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      
      // Update only the lowStock property, keeping other metrics unchanged
      setMetrics(prevMetrics => ({
        ...prevMetrics,
        lowStock: data.lowStockProducts
      }));
    } catch (error) {
      console.error('Error fetching low stock products count:', error);
      // Set a notification for the error
      setNotification({
        show: true,
        message: 'Failed to load low stock products count',
        type: 'error'
      });
    } finally {
      // Set loading state to false when fetch completes (regardless of success/failure)
      setLoading(prev => ({ ...prev, lowStock: false }));
    }
  };
  
  // Function to fetch dashboard announcements
  const fetchDashboardAnnouncements = async () => {
    // Set loading state to true when fetch starts
    setLoading(prev => ({ ...prev, announcements: true }));
    
    try {
      const response = await fetch('https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-announcements-get?dashboard=true');
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      setDashboardAnnouncements(data);
    } catch (error) {
      console.error('Error fetching dashboard announcements:', error);
      // Set a notification for the error
      setNotification({
        show: true,
        message: 'Failed to load announcements',
        type: 'error'
      });
    } finally {
      // Set loading state to false when fetch completes
      setLoading(prev => ({ ...prev, announcements: false }));
    }
  };

  // Set up intervals for fetching data based on dashboard active state
  useEffect(() => {
    // Clear any existing intervals
    if (intervalsRef.current.productsInterval) {
      clearInterval(intervalsRef.current.productsInterval);
    }
    if (intervalsRef.current.lowStockInterval) {
      clearInterval(intervalsRef.current.lowStockInterval);
    }
    if (intervalsRef.current.announcementsInterval) {
      clearInterval(intervalsRef.current.announcementsInterval);
    }
    
    // Set refresh rate based on whether dashboard is active
    const refreshRate = isDashboardActive && activeSection === 'dashboard' 
      ? FAST_REFRESH_RATE 
      : SLOW_REFRESH_RATE;
    
    // Only set up intervals if the dashboard is the active section
    if (activeSection === 'dashboard') {
      // Set up new intervals
      intervalsRef.current.productsInterval = setInterval(fetchTotalProducts, refreshRate);
      intervalsRef.current.lowStockInterval = setInterval(fetchLowStockCount, refreshRate);
      intervalsRef.current.announcementsInterval = setInterval(fetchDashboardAnnouncements, refreshRate);
      
      // Initial fetch if needed
      if (!metrics.totalProducts) fetchTotalProducts();
      if (!metrics.lowStock) fetchLowStockCount();
      if (dashboardAnnouncements.length === 0) fetchDashboardAnnouncements();
    }
    
    // Cleanup function to clear intervals
    return () => {
      clearInterval(intervalsRef.current.productsInterval);
      clearInterval(intervalsRef.current.lowStockInterval);
      clearInterval(intervalsRef.current.announcementsInterval);
    };
  }, [isDashboardActive, activeSection, metrics.totalProducts, metrics.lowStock, dashboardAnnouncements.length]);
  
  // Load dashboard data (for demo purposes - in a real app, this initial data would come from an API)
  useEffect(() => {
    // This simulated data is just for initial render before inventory component loads
    const timer = setTimeout(() => {
      // Sample low stock items for dashboard display
      const sampleLowStockItems = [
        { id: 'v003', name: 'Organic Kale', category: 'Vegetable', stock: 8, unit: 'bunch', status: 'Low Stock' },
        { id: 'd001', name: 'Farm Fresh Eggs', category: 'Dairy', stock: 6, unit: 'dozen', status: 'Low Stock' },
        { id: 'b001', name: 'Zucchini Bread', category: 'Bakery', stock: 0, unit: 'loaf', status: 'Out of Stock' }
      ];
      
      setLowStockItems(sampleLowStockItems);
    }, 600);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Update low stock items whenever inventory data changes
  useEffect(() => {
    if (inventoryData.length > 0) {
      const lowItems = inventoryData
        .filter(item => item.stock < 10)
        .sort((a, b) => a.stock - b.stock)
        .slice(0, 5);
      
      setLowStockItems(lowItems);
    }
  }, [inventoryData]);
  
  // Format currency
  const formatCurrency = (value) => {
    return `$${value.toFixed(2)}`;
  };
  
  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Format time for last updated display
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  
  // Get current date
  const currentDate = formatDate(new Date());
  
  // Get status class for styling low stock items in dashboard
  const getStatusClass = (status) => {
    switch (status) {
      case 'In Stock':
        return 'status-instock';
      case 'Low Stock':
        return 'status-lowstock';
      case 'Out of Stock':
        return 'status-outofstock';
      default:
        return '';
    }
  };
  
  // CSS for spinner
  const spinnerStyle = {
    display: 'inline-block',
    width: '20px',
    height: '20px',
    border: '2px solid rgba(166, 124, 82, 0.2)',
    borderTop: '2px solid var(--dashboard-primary)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginRight: '8px',
    verticalAlign: 'middle'
  };

  // Refresh button
  const RefreshButton = ({ onClick, isLoading }) => (
    <button 
      className="refresh-btn" 
      onClick={onClick}
      disabled={isLoading}
      title="Refresh data"
    >
      {isLoading ? (
        <span className="spinner-container">
          <span style={spinnerStyle}></span>
        </span>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 4v6h-6"></path>
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
        </svg>
      )}
    </button>
  );
  
  return (
    <div className="bucks-admin-dashboard">
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h1 className="sidebar-logo">Buck's Farmstand</h1>
          <p className="sidebar-subtitle">Admin Portal</p>
        </div>
        
        <nav className="sidebar-nav">
          <ul>
            <li>
              <button 
                className={`nav-link ${activeSection === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActiveSection('dashboard')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="7" height="9" x="3" y="3" rx="1"></rect>
                  <rect width="7" height="5" x="14" y="3" rx="1"></rect>
                  <rect width="7" height="9" x="14" y="12" rx="1"></rect>
                  <rect width="7" height="5" x="3" y="16" rx="1"></rect>
                </svg>
                Dashboard
              </button>
            </li>
            <li>
              <button 
                className={`nav-link ${activeSection === 'inventory' ? 'active' : ''}`}
                onClick={() => setActiveSection('inventory')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 5H8.94a2.1 2.1 0 0 0-1.94 1.35L2 18"></path>
                  <path d="M20 5v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-1"></path>
                  <path d="M15 9h-2a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h2v0a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-2"></path>
                </svg>
                Inventory
              </button>
            </li>
            <li>
              <button 
                className={`nav-link ${activeSection === 'orders' ? 'active' : ''}`}
                onClick={() => setActiveSection('orders')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path>
                  <path d="M3 6h18"></path>
                  <path d="M16 10a4 4 0 0 1-8 0"></path>
                </svg>
                Orders
              </button>
            </li>
            <li>
              <button 
                className={`nav-link ${activeSection === 'recipes' ? 'active' : ''}`}
                onClick={() => setActiveSection('recipes')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z"></path>
                  <line x1="6" y1="17" x2="18" y2="17"></line>
                </svg>
                Recipes
              </button>
            </li>
            <li>
              <button 
                className={`nav-link ${activeSection === 'workers' ? 'active' : ''}`}
                onClick={() => setActiveSection('workers')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                Workers
              </button>
            </li>

            <li>
              <button 
                className={`nav-link ${activeSection === 'customers' ? 'active' : ''}`}
                onClick={() => setActiveSection('customers')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                Customers
              </button>
            </li>
            <li>
              <button 
                className={`nav-link ${activeSection === 'announcements' ? 'active' : ''}`}
                onClick={() => setActiveSection('announcements')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <path d="M16 13H8"></path>
                  <path d="M16 17H8"></path>
                  <path d="M10 9H8"></path>
                </svg>
                Announcements
              </button>
            </li>
            <li>
              <button 
                className={`nav-link ${activeSection === 'reports' ? 'active' : ''}`}
                onClick={() => setActiveSection('reports')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                </svg>
                Reports
              </button>
            </li>
          </ul>
        </nav>
        
        <div className="sidebar-footer">
          <button className="logout-btn" onClick={logout}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            Logout
          </button>
        </div>
      </aside>
      
      <main className="admin-main">
        <header className="admin-header">
          <div className="header-title">
            <h1>
              {activeSection === 'dashboard' && 'Dashboard'}
              {activeSection === 'inventory' && 'Inventory Management'}
              {activeSection === 'orders' && 'Orders Management'}
              {activeSection === 'recipes' && 'Recipe Management'}
              {activeSection === 'workers' && 'Worker Time Tracking'}
              {activeSection === 'customers' && 'Customer Management'}
              {activeSection === 'reports' && 'Reports'}
              {activeSection === 'announcements' && 'Announcements Management'}
            </h1>
            <p className="header-date">Today is {currentDate}</p>
          </div>
          
          <div className="header-actions">
            {activeSection === 'dashboard' && (
              <div className="dashboard-refresh">
                <span className="last-updated">
                  Last updated: {formatTime(lastUpdated)}
                </span>
                <RefreshButton 
                  onClick={refreshAllDashboardData}
                  isLoading={loading.totalProducts || loading.lowStock || loading.announcements}
                />
              </div>
            )}
            <div className="admin-user">
              <div className="user-avatar">
                <span>{adminUser?.name?.charAt(0) || 'A'}</span>
              </div>
              <div className="user-info">
                <p className="user-name">{adminUser?.name || 'Admin User'}</p>
                <p className="user-role">Farm Manager</p>
              </div>
            </div>
          </div>
        </header>
        
        {notification.show && (
          <div className={`notification ${notification.type}`}>
            <span className="notification-message">{notification.message}</span>
            <button 
              className="close-notification" 
              onClick={() => setNotification({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        )}
        
        {activeSection === 'dashboard' && (
          <div className="dashboard-section">
            <div className="dashboard-metrics">
              <div className="metric-card">
                <div className="metric-icon inventory-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 5H8.94a2.1 2.1 0 0 0-1.94 1.35L2 18"></path>
                    <path d="M20 5v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-1"></path>
                  </svg>
                </div>
                <div className="metric-content">
                  <h3>Total Products</h3>
                  <p className="metric-value">
                    {loading.totalProducts ? (
                      <span>
                        <span className="metric-spinner" style={spinnerStyle}></span>
                        Loading...
                      </span>
                    ) : (
                      metrics.totalProducts
                    )}
                  </p>
                  <p className="metric-description">Products in inventory</p>
                </div>
                <div className="metric-refresh">
                  <RefreshButton 
                    onClick={fetchTotalProducts}
                    isLoading={loading.totalProducts}
                  />
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon lowstock-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <div className="metric-content">
                  <h3>Low Stock</h3>
                  <p className="metric-value">
                    {loading.lowStock ? (
                      <span>
                        <span className="metric-spinner" style={spinnerStyle}></span>
                        Loading...
                      </span>
                    ) : (
                      metrics.lowStock
                    )}
                  </p>
                  <p className="metric-description">Products need restocking</p>
                </div>
                <div className="metric-refresh">
                  <RefreshButton 
                    onClick={fetchLowStockCount}
                    isLoading={loading.lowStock}
                  />
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon value-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div className="metric-content">
                  <h3>Inventory Value</h3>
                  <p className="metric-value">{formatCurrency(metrics.totalValue)}</p>
                  <p className="metric-description">Total value of stock</p>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon orders-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path>
                    <path d="M3 6h18"></path>
                    <path d="M16 10a4 4 0 0 1-8 0"></path>
                  </svg>
                </div>
                <div className="metric-content">
                  <h3>Recent Orders</h3>
                  <p className="metric-value">{metrics.recentOrders}</p>
                  <p className="metric-description">Orders in the last 24h</p>
                </div>
              </div>
            </div>
            
            <div className="dashboard-tables">
              <div className="dashboard-table-container">
                <div className="table-header">
                  <h2>Low Stock Items</h2>
                  <div className="table-actions">
                    <RefreshButton 
                      onClick={fetchLowStockCount}
                      isLoading={loading.lowStock}
                    />
                    <button className="view-all-btn" onClick={() => setActiveSection('inventory')}>
                      View All
                    </button>
                  </div>
                </div>
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Category</th>
                      <th>Stock</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStockItems.length > 0 ? (
                      lowStockItems.map(item => (
                        <tr key={item.id}>
                          <td>{item.name}</td>
                          <td>{item.category}</td>
                          <td>{item.stock} {item.unit}</td>
                          <td>
                            <span className={`status-badge ${getStatusClass(item.status)}`}>
                              {item.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center' }}>No low stock items</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="dashboard-table-container">
                <div className="table-header">
                  <h2>Recent Announcements</h2>
                  <div className="table-actions">
                    <RefreshButton 
                      onClick={fetchDashboardAnnouncements}
                      isLoading={loading.announcements}
                    />
                    <button className="view-all-btn" onClick={() => setActiveSection('announcements')}>
                      Manage All
                    </button>
                  </div>
                </div>
                <div className="activity-list">
                  {loading.announcements ? (
                    <div className="loading-container" style={{ textAlign: 'center', padding: '2rem 0' }}>
                      <span style={spinnerStyle}></span>
                      <span>Loading announcements...</span>
                    </div>
                  ) : dashboardAnnouncements.length > 0 ? (
                    dashboardAnnouncements.map(announcement => (
                      <div className="announcement-card" key={announcement.id || Math.random().toString()}>
                        <div className="announcement-card-header">
                          <h3 className="announcement-card-title">{announcement.title || 'Untitled Announcement'}</h3>
                          <span className={`status-badge tag-${(announcement.tag || 'update').toLowerCase()} announcement-card-tag`}>
                            {announcement.tag || 'Update'}
                          </span>
                          <div className="announcement-card-priority">{announcement.priority || 1}</div>
                        </div>
                        <p className="announcement-card-summary">{announcement.summary || 'No description available'}</p>
                        <div className="announcement-card-dates">
                          <span>
                            {announcement.publish_start && announcement.publish_end 
                              ? `${formatDate(announcement.publish_start)} - ${formatDate(announcement.publish_end)}`
                              : `From: ${formatDate(announcement.publish_start || new Date())}`}
                          </span>
                          <span>Status: {announcement.status || 'Draft'}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="announcement-card">
                      <p className="announcement-card-summary" style={{ textAlign: 'center' }}>No announcements available</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="dashboard-table-container">
                <div className="table-header">
                  <h2>Recent Activity</h2>
                </div>
                <div className="activity-list">
                  <div className="activity-item">
                    <div className="activity-icon add-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14"></path>
                        <path d="M12 5v14"></path>
                      </svg>
                    </div>
                    <div className="activity-content">
                      <p className="activity-text">Added new product <strong>Seasonal Berries</strong></p>
                      <p className="activity-time">Today, 9:15 AM</p>
                    </div>
                  </div>
                  
                  <div className="activity-item">
                    <div className="activity-icon update-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </div>
                    <div className="activity-content">
                      <p className="activity-text">Updated stock for <strong>Farm Fresh Eggs</strong></p>
                      <p className="activity-time">Today, 8:30 AM</p>
                    </div>
                  </div>
                  
                  <div className="activity-item">
                    <div className="activity-icon order-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path>
                        <path d="M3 6h18"></path>
                        <path d="M16 10a4 4 0 0 1-8 0"></path>
                      </svg>
                    </div>
                    <div className="activity-content">
                      <p className="activity-text">New order <strong>#1234</strong> received</p>
                      <p className="activity-time">Yesterday, 4:25 PM</p>
                    </div>
                  </div>
                  
                  <div className="activity-item">
                    <div className="activity-icon delete-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18"></path>
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                      </svg>
                    </div>
                    <div className="activity-content">
                      <p className="activity-text">Removed product <strong>Winter Squash</strong></p>
                      <p className="activity-time">Yesterday, 2:10 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Render inventory component when inventory tab is active */}
        {activeSection === 'inventory' && (
          <BucksAdminInventory 
            updateMetrics={updateMetrics} 
            updateInventoryData={updateInventoryData}
          />
        )}
        
        {/* Render orders component when orders tab is active */}
        {activeSection === 'orders' && (
          <BucksAdminOrderManage />
        )}
        
        {/* Render recipes component when recipes tab is active */}
        {activeSection === 'recipes' && (
          <BucksAdminRecipes />
        )}

        {activeSection === 'workers' && (
          <BucksAdminWorkers />
        )}
        
        {/* Render customer management component when customers tab is active */}
        {activeSection === 'customers' && (
          <BucksAdminCustomerManage />
        )}
        
        {/* Render announcements component when announcements tab is active */}
        {activeSection === 'announcements' && (
          <BucksAdminAnnouncements />
        )}
        
        {activeSection === 'reports' && (
          <BucksAdminReports />
        )}
      </main>

      {/* Add CSS for the spinner animation and refresh button */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .refresh-btn {
            background: none;
            border: none;
            color: var(--dashboard-primary);
            cursor: pointer;
            padding: 6px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: var(--dashboard-transition);
          }
          
          .refresh-btn:hover {
            background-color: rgba(166, 124, 82, 0.1);
          }
          
          .refresh-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          
          .dashboard-refresh {
            display: flex;
            align-items: center;
            margin-right: 1.5rem;
          }
          
          .last-updated {
            font-size: 0.85rem;
            color: var(--dashboard-text-light);
            margin-right: 0.5rem;
          }
          
          .header-actions {
            display: flex;
            align-items: center;
          }
          
          .table-actions {
            display: flex;
            align-items: center;
          }
          
          .table-actions .refresh-btn {
            margin-right: 0.5rem;
          }
          
          .metric-refresh {
            margin-left: auto;
            padding-left: 0.5rem;
          }
          
          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100px;
          }
        `}
      </style>
    </div>
  );
}

export default BucksAdminDashboard;